import { SalaryScaleGroupService, ISalaryScaleGroup } from "../../../services/salaryScaleGroup.service";

class TimezonesListController implements ng.IComponentController {
    sortType: string;
    salaryScaleGroups: ISalaryScaleGroup[];
    selectedGroup: ISalaryScaleGroup;
    search: string;
    timezones: any[];

    private weekDays = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];

    constructor(
        private SalaryScaleGroupService: SalaryScaleGroupService,
        private $state: ng.ui.IStateService,
        private $stateParams: ng.ui.IStateParamsService
    ) {
        'ngInject';

        this.sortType = 'priority'; // Default sort op prioriteit (zoals backend)
    }

    public $onInit = () => {
        this.loadSalaryScaleGroups();
    }

    private loadSalaryScaleGroups = () => {
        this.SalaryScaleGroupService.all().then((result: ISalaryScaleGroup[]) => {
            // Sort alphabetically by name
            this.salaryScaleGroups = result.sort((a, b) => {
                if (a.name < b.name) return -1;
                if (a.name > b.name) return 1;
                return 0;
            });
            
            // Restore selectedGroup from state params if available
            const selectedGroupId = this.$stateParams['selectedCaoId'];
            if (selectedGroupId) {
                const group = this.salaryScaleGroups.find(g => g.id === selectedGroupId);
                if (group) {
                    this.selectedGroup = group;
                }
            } else if (this.selectedGroup) {
                // Update selectedGroup als die nog bestaat
                const updatedGroup = this.salaryScaleGroups.find(g => g.id === this.selectedGroup.id);
                if (updatedGroup) {
                    this.selectedGroup = updatedGroup;
                }
            }
        });
    }

    public clearSelection = () => {
        this.selectedGroup = null;
        // Clear selected CAO ID from state params
        this.$state.go('settings.timezones.list', { 'selectedCaoId': null }, { location: 'replace', notify: false });
    }

    public onGroupChange = (selectedGroup: ISalaryScaleGroup) => {
        this.selectedGroup = selectedGroup;
        // Save selected CAO ID to state params
        this.$state.go('settings.timezones.list', { 'selectedCaoId': selectedGroup ? selectedGroup.id : null }, { location: 'replace', notify: false });
    }

    public addTimezone = () => {
        if (!this.selectedGroup || !this.selectedGroup.id) {
            alert('Geen CAO geselecteerd. Selecteer eerst een CAO om een timezone toe te voegen.');
            return;
        }
        this.$state.go('settings.timezones.list.item', { 'id': '', 'caoId': this.selectedGroup.id });
    }

    public caoFilter = (item: any) => {
        if (!this.selectedGroup || !this.selectedGroup.id) {
            return false;
        }
        return item.salaryScaleGroupId === this.selectedGroup.id;
    }

    /**
     * Berekent de prioriteit van een timezone volgens backend logica
     * 1 = Specifieke datum (hoogste prioriteit)
     * 2 = Vaste dag elk jaar
     * 3 = Specifieke weekdag
     * 4 = Elke dag (laagste prioriteit)
     */
    public getTimezonePriority = (item: any): number => {
        if (item.fixedDate && !item.fixedDateWithoutYear) {
            return 1; // Specifieke datum
        } else if (item.fixedDateWithoutYear) {
            return 2; // Vaste dag elk jaar
        } else if (item.dayOfWeek != null) {
            return 3; // Specifieke weekdag
        } else {
            return 4; // Elke dag
        }
    }

    /**
     * Geeft een label terug voor het type timezone
     */
    public getTimezoneTypeLabel = (item: any): string => {
        if (item.fixedDate && !item.fixedDateWithoutYear) {
            return 'Specifieke datum';
        } else if (item.fixedDateWithoutYear) {
            return 'Vaste dag elk jaar';
        } else if (item.dayOfWeek != null) {
            return 'Weekdag';
        } else {
            return 'Dagelijks';
        }
    }

    /**
     * Geeft een CSS class terug voor de prioriteit badge
     */
    public getPriorityBadgeClass = (item: any): string => {
        const priority = this.getTimezonePriority(item);
        switch (priority) {
            case 1: return 'label-danger'; // Rood - hoogste prioriteit
            case 2: return 'label-warning'; // Oranje
            case 3: return 'label-info'; // Blauw
            case 4: return 'label-default'; // Grijs - laagste prioriteit
            default: return 'label-default';
        }
    }

    /**
     * Custom sort functie die eerst op prioriteit sorteert, dan op tijd
     */
    public prioritySort = (item: any): any => {
        const priority = this.getTimezonePriority(item);
        // Sorteer eerst op prioriteit (1-4), dan op tijd (from)
        const fromTime = item.from ? new Date(item.from).getTime() : 0;
        return `${priority}-${fromTime}`;
    }

    /**
     * Geeft de prioriteit header informatie terug
     */
    public getPriorityHeaderInfo = (priority: number): {label: string, class: string, icon: string} => {
        switch (priority) {
            case 1: 
                return {
                    label: 'Specifieke datum (hoogste prioriteit)',
                    class: 'panel-danger',
                    icon: 'fa-calendar-check-o'
                };
            case 2: 
                return {
                    label: 'Vaste dag elk jaar',
                    class: 'panel-warning',
                    icon: 'fa-calendar'
                };
            case 3: 
                return {
                    label: 'Specifieke weekdag',
                    class: 'panel-info',
                    icon: 'fa-calendar-o'
                };
            case 4: 
                return {
                    label: 'Dagelijks (laagste prioriteit)',
                    class: 'panel-default',
                    icon: 'fa-calendar-times-o'
                };
            default: 
                return {
                    label: 'Onbekend',
                    class: 'panel-default',
                    icon: 'fa-question'
                };
        }
    }

    /**
     * Filter functie om timezones per prioriteit te krijgen
     */
    public getTimezonesByPriority = (items: any[], priority: number, search: string): any[] => {
        if (!items) return [];
        
        let filtered = items.filter(item => {
            // CAO filter
            if (!this.selectedGroup || !this.selectedGroup.id) {
                return false;
            }
            if (item.salaryScaleGroupId !== this.selectedGroup.id) {
                return false;
            }
            
            // Prioriteit filter
            if (this.getTimezonePriority(item) !== priority) {
                return false;
            }
            
            // Search filter
            if (search) {
                const searchLower = search.toLowerCase();
                const hourCodeName = (item.hourCodeName || '').toLowerCase();
                const fromDate = item.from ? new Date(item.from).toLocaleString('nl-NL') : '';
                const tillDate = item.till ? new Date(item.till).toLocaleString('nl-NL') : '';
                const percentage = (item.percentage || '').toString();
                
                if (!hourCodeName.includes(searchLower) && 
                    !fromDate.includes(searchLower) && 
                    !tillDate.includes(searchLower) && 
                    !percentage.includes(searchLower)) {
                    return false;
                }
            }
            
            return true;
        });
        
        // Sorteer binnen de groep
        return filtered.sort((a, b) => {
            // Voor prioriteit 3 (specifieke weekdag): eerst op weekdag, dan op tijd
            if (priority === 3) {
                const dayA = a.dayOfWeek != null ? a.dayOfWeek : 999;
                const dayB = b.dayOfWeek != null ? b.dayOfWeek : 999;
                
                // Eerst sorteren op weekdag (0 = zondag, 1 = maandag, etc.)
                if (dayA !== dayB) {
                    return dayA - dayB;
                }
                
                // Als dezelfde weekdag, dan sorteren op tijd
                const timeA = a.from ? new Date(a.from).getTime() : 0;
                const timeB = b.from ? new Date(b.from).getTime() : 0;
                return timeA - timeB;
            } else {
                // Voor andere prioriteiten: sorteren op tijd (from)
                const timeA = a.from ? new Date(a.from).getTime() : 0;
                const timeB = b.from ? new Date(b.from).getTime() : 0;
                return timeA - timeB;
            }
        });
    }

    /**
     * Check of er timezones zijn voor een bepaalde prioriteit
     */
    public hasTimezonesForPriority = (priority: number): boolean => {
        if (!this.timezones) return false;
        return this.getTimezonesByPriority(this.timezones, priority, this.search || '').length > 0;
    }
}

export const timezonesListComponent: ng.IComponentOptions = {
    controller: TimezonesListController,
    bindings: {
        timezones:'<'
    },
    template: require('./timezones.list.html')
};


// WEBPACK FOOTER //
// ./src/app/modules/settings/timezones/timezones.list.component.ts